// src/pages/EditPage.js
import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Paper } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { db } from '../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';

import EditableImportsTable from '../components/EditableImportsTable';
import EditableExportsTable from '../components/EditableExportsTable';
import ImportDialog from '../components/ImportDialog';
import ExportDialog from '../components/ExportDialog';
import PrintDialog from '../components/PrintDialog';

function EditPage() {
  const [tabIndex, setTabIndex] = useState(0);
  const { currentUser } = useAuth();
  const { imports, exports, products, suppliers, customers, drivers, assistantDrivers, vehicles, warehouses } = useAppContext();

  const [editingItem, setEditingItem] = useState(null);
  const [dialogType, setDialogType] = useState(null); // 'import', 'export', 'print_import', 'print_export'
  const [orderToPrint, setOrderToPrint] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleEdit = (item, type) => {
    setEditingItem(item);
    setDialogType(type);
  };

  const handleCloseDialog = () => {
    setEditingItem(null);
    setDialogType(null);
    setOrderToPrint(null);
  };

  const handleSaveImport = async (importData) => {
    try {
        await runTransaction(db, async (transaction) => {
            const importDocRef = doc(db, "imports", editingItem.id);
            const deltaMap = new Map();
            (editingItem.items || []).forEach(item => {
                deltaMap.set(item.productId, (deltaMap.get(item.productId) || 0) - item.quantity);
            });
            importData.items.forEach(item => {
                deltaMap.set(item.productId, (deltaMap.get(item.productId) || 0) + item.quantity);
            });
            const productRefs = Array.from(deltaMap.keys()).map(pid => doc(db, "products", pid));
            const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));
            productDocs.forEach((productDoc, index) => {
                const productId = productRefs[index].id;
                const delta = deltaMap.get(productId);
                if (delta !== 0) {
                    if (!productDoc.exists()) throw new Error("Sản phẩm không tồn tại");
                    const newQuantity = (productDoc.data().quantity || 0) + delta;
                    transaction.update(productRefs[index], { quantity: newQuantity });
                }
            });
            transaction.update(importDocRef, { ...importData, updatedAt: serverTimestamp() });
        });
        alert('Cập nhật phiếu nhập thành công!');
        const dataForPrint = { ...editingItem, ...importData };
        setOrderToPrint(dataForPrint);
        setEditingItem(null);
        setDialogType('print_import');
    } catch (e) {
        console.error("Lỗi khi cập nhật phiếu nhập: ", e);
        alert(`Đã có lỗi xảy ra: ${e.message}`);
    }
  };

  const handleDeleteImport = async (item) => {
    if (window.confirm(`Bạn có chắc muốn XÓA VĨNH VIỄN phiếu nhập ${item.poNumber}? Hành động này sẽ trừ hàng khỏi kho và không thể hoàn tác.`)) {
        try {
            await runTransaction(db, async (transaction) => {
                const importDocRef = doc(db, "imports", item.id);
                if (item.items && item.items.length > 0) {
                    const productRefs = item.items.map(p => doc(db, "products", p.productId));
                    const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));
                    productDocs.forEach((pDoc, index) => {
                        if (pDoc.exists()) {
                            const currentQty = pDoc.data().quantity || 0;
                            const importQty = item.items[index].quantity;
                            transaction.update(productRefs[index], { quantity: currentQty - importQty });
                        }
                    });
                }
                transaction.delete(importDocRef);
            });
            alert("Xóa phiếu nhập thành công!");
        } catch (e) {
            console.error("Lỗi khi xóa phiếu nhập:", e);
            alert(`Lỗi: ${e.message}`);
        }
    }
  };

  const handleSaveExport = async (exportData) => {
    try {
        await runTransaction(db, async (transaction) => {
            const exportDocRef = doc(db, "exports", editingItem.id);
            const deltaMap = new Map();
            (editingItem.items || []).forEach(item => { deltaMap.set(item.productId, (deltaMap.get(item.productId) || 0) + item.quantity); });
            exportData.items.forEach(item => { deltaMap.set(item.productId, (deltaMap.get(item.productId) || 0) - item.quantity); });
            const productRefs = Array.from(deltaMap.keys()).map(pid => doc(db, "products", pid));
            const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));
            const currentStockMap = new Map(productDocs.map(d => [d.id, d.data()?.quantity || 0]));
            for (const [productId, delta] of deltaMap.entries()) {
                if (delta !== 0) {
                    const productRef = doc(db, "products", productId);
                    const newQuantity = (currentStockMap.get(productId) || 0) + delta;
                    transaction.update(productRef, { quantity: newQuantity });
                }
            }
            transaction.update(exportDocRef, { ...exportData, updatedAt: serverTimestamp() });
        });
        alert('Cập nhật phiếu xuất thành công!');
        const dataForPrint = { ...editingItem, ...exportData };
        setOrderToPrint(dataForPrint);
        setEditingItem(null);
        setDialogType('print_export');
    } catch (e) {
        console.error("Lỗi khi cập nhật phiếu xuất: ", e);
        alert(`Đã có lỗi xảy ra: ${e.message}`);
    }
  };

  const handleDeleteExport = async (item) => {
    if (window.confirm(`Bạn có chắc muốn HỦY phiếu xuất ${item.soNumber}? Hành động này sẽ hoàn trả hàng về kho.`)) {
        try {
            await runTransaction(db, async (transaction) => {
                const exportDocRef = doc(db, "exports", item.id);
                if (item.items && item.items.length > 0) {
                    const productRefs = item.items.map(p => doc(db, "products", p.productId));
                    const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));
                    productDocs.forEach((pDoc, index) => {
                        if (pDoc.exists()) {
                            const currentQty = pDoc.data().quantity || 0;
                            const exportQty = item.items[index].quantity;
                            transaction.update(productRefs[index], { quantity: currentQty + exportQty });
                        }
                    });
                }
                transaction.delete(exportDocRef);
            });
            alert("Hủy phiếu xuất thành công!");
        } catch (e) {
            console.error("Lỗi khi hủy phiếu xuất:", e);
            alert(`Lỗi: ${e.message}`);
        }
    }
  };

  const isSuperUser = currentUser?.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser?.permissions?.full_access;
  const canEditApprovedImports = isSuperUser || currentUser?.permissions?.editApprovedImports;
  const canEditApprovedExports = isSuperUser || currentUser?.permissions?.editApprovedExports;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Chỉnh Sửa Phiếu Nhập & Xuất (Đã Duyệt)
      </Typography>
      <Paper>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="Chỉnh Sửa Phiếu Nhập" />
          <Tab label="Chỉnh Sửa Phiếu Xuất" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tabIndex === 0 && (
            <EditableImportsTable 
              imports={imports.filter(i => i.approvedBy)}
              onEdit={(item) => handleEdit(item, 'import')}
              onDelete={handleDeleteImport}
              canEdit={canEditApprovedImports}
              canDelete={canEditApprovedImports}
            />
          )}
          {tabIndex === 1 && (
            <EditableExportsTable 
              exports={exports.filter(e => e.approvedBy)}
              onEdit={(item) => handleEdit(item, 'export')}
              onDelete={handleDeleteExport}
              canEdit={canEditApprovedExports}
              canDelete={canEditApprovedExports}
            />
          )}
        </Box>
      </Paper>

      {/* Dialogs */}
      {dialogType === 'import' && (
        <ImportDialog
            open={!!editingItem}
            onClose={handleCloseDialog}
            onSave={handleSaveImport}
            products={products}
            suppliers={suppliers}
            initialImportOrder={editingItem}
        />
      )}
      {dialogType === 'export' && (
        <ExportDialog
            open={!!editingItem}
            onClose={handleCloseDialog}
            onSave={handleSaveExport}
            products={products}
            customers={customers}
            drivers={drivers}
            assistantDrivers={assistantDrivers}
            vehicles={vehicles}
            warehouses={warehouses}
            initialExportOrder={editingItem}
        />
      )}
      <PrintDialog 
        open={dialogType === 'print_import' || dialogType === 'print_export'}
        onClose={handleCloseDialog}
        orderData={orderToPrint}
        type={dialogType === 'print_import' ? 'import' : 'export'}
      />
    </Box>
  );
}

export default EditPage;
